import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import { formatINR } from "../lib/utils-format";
import { toast } from "sonner";
import { Loader2, Play, Users, Wallet, CalendarClock } from "lucide-react";

export default function AdminHome() {
    const [stats, setStats] = useState(null);
    const [running, setRunning] = useState(false);

    const loadStats = async () => {
        const { data } = await api.get("/admin/stats");
        setStats(data);
    };

    useEffect(() => { loadStats(); }, []);

    const runPairing = async () => {
        setRunning(true);
        try {
            const { data } = await api.post("/pairing/admin/run");
            toast.success(`Pairing run ${data.status}`, {
                description: `${data.pairs_credited} pair(s) credited, ${data.users_processed} user(s) processed.`,
            });
            await loadStats();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Run failed");
        } finally {
            setRunning(false);
        }
    };

    return (
        <AppShell admin>
            <div className="mb-6">
                <div className="overline">Admin</div>
                <h1 className="font-display font-black text-3xl tracking-tighter text-slate-900 mt-2">Platform overview</h1>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="stat-card" data-testid="admin-stat-users">
                    <div className="overline flex items-center gap-1"><Users className="w-3 h-3 text-[#2563EB]" /> Users</div>
                    <div className="font-display font-black text-3xl mt-2">{stats?.total_users ?? "—"}</div>
                    <div className="text-xs text-slate-500 mt-1">{stats?.active_users ?? 0} active</div>
                </div>
                <div className="stat-card stat-card-amber" data-testid="admin-stat-revenue">
                    <div className="overline flex items-center gap-1"><Wallet className="w-3 h-3" /> Revenue</div>
                    <div className="font-display font-black text-3xl mt-2">{formatINR(stats?.total_revenue)}</div>
                </div>
                <div className="stat-card stat-card-left" data-testid="admin-stat-orders">
                    <div className="overline flex items-center gap-1">Orders</div>
                    <div className="font-display font-black text-3xl mt-2">{stats?.total_orders ?? "—"}</div>
                </div>
                <div className="stat-card stat-card-right" data-testid="admin-stat-bonus">
                    <div className="overline">Member payouts</div>
                    <div className="font-display font-black text-3xl mt-2">{formatINR(stats?.total_bonus_paid)}</div>
                    <div className="text-xs text-slate-500 mt-1">direct + pairing + travel fund</div>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                <div className="stat-card">
                    <div className="overline">Pairing engine</div>
                    <div className="mt-2 font-display font-bold text-xl">Nightly at midnight IST</div>
                    {stats?.last_pairing_run ? (
                        <div className="mt-3 text-sm text-slate-600">
                            Last run: <span className="font-mono font-bold text-slate-900">{stats.last_pairing_run.run_date}</span> — {stats.last_pairing_run.pairs_credited} pair(s) credited ({stats.last_pairing_run.status})
                        </div>
                    ) : (
                        <div className="mt-3 text-sm text-slate-500">No runs yet.</div>
                    )}
                    <div className="mt-4 flex flex-wrap gap-3">
                        <button
                            onClick={runPairing}
                            disabled={running}
                            data-testid="admin-run-pairing-btn"
                            className="px-5 h-11 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold flex items-center gap-2 disabled:opacity-70"
                        >
                            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                            Run pairing now
                        </button>
                        <Link to="/admin/pairing" className="px-5 h-11 rounded-lg border border-slate-200 hover:border-slate-300 text-slate-700 font-semibold flex items-center gap-2">
                            <CalendarClock className="w-4 h-4" /> View runs
                        </Link>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="overline">Quick actions</div>
                    <div className="mt-4 grid gap-3">
                        <Link to="/admin/users" className="flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-[#2563EB] hover:bg-blue-50 transition-colors">
                            <div>
                                <div className="font-semibold">Manage users</div>
                                <div className="text-xs text-slate-500">Activate, suspend, inspect accounts</div>
                            </div>
                            <Users className="w-4 h-4 text-slate-400" />
                        </Link>
                    </div>
                </div>
            </div>
        </AppShell>
    );
}
